import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, ArrowLeft, Save, Plus, Trash2, GripVertical, Loader2 } from "lucide-react";
import { C } from "./sopsUi";

const emptyForm = {
  title: "", category: "getting_started", summary: "", audiences: ["all_staff"],
  related_routes: "", steps: [{ title: "", detail: "" }], warnings: "",
  role_requirements: "", next_steps: "", keywords: "", links: [], screenshots: "", videos: "",
};

export default function SopEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sops/meta").then(({ data }) => setMeta(data)).catch(() => toast.error("Could not load options."));
    if (editing) {
      api.get(`/sops/${id}`).then(({ data }) => {
        setForm({
          title: data.title || "", category: data.category || "getting_started",
          summary: data.summary || "", audiences: data.audiences?.length ? data.audiences : ["all_staff"],
          related_routes: (data.related_routes || []).join("\n"),
          steps: data.steps?.length ? data.steps : [{ title: "", detail: "" }],
          warnings: (data.warnings || []).join("\n"),
          role_requirements: data.role_requirements || "", next_steps: data.next_steps || "",
          keywords: data.keywords || "", links: data.links || [],
          screenshots: (data.screenshots || []).join("\n"), videos: (data.videos || []).join("\n"),
        });
      }).catch((e) => { toast.error(errMsg(e)); navigate("/sops"); }).finally(() => setLoading(false));
    }
  }, [id, editing, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAud = (a) => setForm((f) => ({ ...f, audiences: f.audiences.includes(a) ? f.audiences.filter((x) => x !== a) : [...f.audiences, a] }));
  const setStep = (i, k, v) => setForm((f) => ({ ...f, steps: f.steps.map((s, j) => j === i ? { ...s, [k]: v } : s) }));
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { title: "", detail: "" }] }));
  const rmStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }));
  const setLink = (i, k, v) => setForm((f) => ({ ...f, links: f.links.map((l, j) => j === i ? { ...l, [k]: v } : l) }));
  const addLink = () => setForm((f) => ({ ...f, links: [...f.links, { label: "", url: "" }] }));
  const rmLink = (i) => setForm((f) => ({ ...f, links: f.links.filter((_, j) => j !== i) }));

  const toLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    if (!form.title.trim()) { toast.error("A title is required."); return; }
    if (!form.audiences.length) { toast.error("Pick at least one audience."); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(), category: form.category, summary: form.summary.trim(),
      audiences: form.audiences, related_routes: toLines(form.related_routes),
      steps: form.steps.filter((s) => s.title.trim() || s.detail.trim()),
      warnings: toLines(form.warnings), role_requirements: form.role_requirements.trim(),
      next_steps: form.next_steps.trim(), keywords: form.keywords.trim(),
      links: form.links.filter((l) => (l.url || "").trim()),
      screenshots: toLines(form.screenshots), videos: toLines(form.videos),
    };
    try {
      const { data } = editing ? await api.put(`/sops/${id}`, payload) : await api.post("/sops", payload);
      toast.success(editing ? "Saved. Publish it from the SOP to make it visible." : "Draft created. Publish it when ready.");
      navigate(`/sops/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (loading || !meta) return <div className="max-w-3xl mx-auto space-y-3"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-64" /></div>;

  return (
    <div className="max-w-3xl mx-auto pb-16" data-testid="sop-editor-page">
      <button onClick={() => navigate(editing ? `/sops/${id}` : "/sops")} className="text-[13px] flex items-center gap-1 mb-3" style={{ color: C.slate }}>
        <ArrowLeft size={14} /> Cancel
      </button>
      <h1 className="font-display text-2xl font-bold flex items-center gap-2 mb-4" style={{ color: C.charcoal }}>
        <BookOpen size={20} style={{ color: C.navy }} /> {editing ? "Edit SOP" : "New SOP"}
      </h1>

      <Card className="p-5 border-[#E6EDF2] space-y-4">
        <div>
          <Label className="text-[12px]" style={{ color: C.slate }}>Title</Label>
          <Input data-testid="sop-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. How to Add a Lead" className="mt-1 h-9" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[12px]" style={{ color: C.slate }}>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-1 h-9 text-[13px]" data-testid="sop-form-category"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[300px]">{meta.categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px]" style={{ color: C.slate }}>Keywords (for search)</Label>
            <Input value={form.keywords} onChange={(e) => set("keywords", e.target.value)} placeholder="lead intake pipeline" className="mt-1 h-9" />
          </div>
        </div>
        <div>
          <Label className="text-[12px]" style={{ color: C.slate }}>Summary</Label>
          <Textarea data-testid="sop-form-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2} className="mt-1 text-[13px]" placeholder="One or two sentences describing this guide." />
        </div>

        <div data-testid="sop-form-audiences">
          <Label className="text-[12px]" style={{ color: C.slate }}>Who can see this (audiences)</Label>
          <div className="mt-1.5 space-y-2">
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {meta.audiences.special.map((a) => (
                <label key={a.value} className="flex items-center gap-1.5 text-[12.5px] cursor-pointer" style={{ color: C.charcoal }}>
                  <Checkbox checked={form.audiences.includes(a.value)} onCheckedChange={() => toggleAud(a.value)} data-testid={`sop-aud-${a.value}`} /> {a.label}
                </label>
              ))}
            </div>
            <details className="text-[12.5px]">
              <summary className="cursor-pointer" style={{ color: C.navy }}>Target specific staff roles</summary>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2">
                {meta.audiences.staff_roles.map((a) => (
                  <label key={a.value} className="flex items-center gap-1.5 text-[12px] cursor-pointer" style={{ color: C.charcoal }}>
                    <Checkbox checked={form.audiences.includes(a.value)} onCheckedChange={() => toggleAud(a.value)} /> {a.label}
                  </label>
                ))}
              </div>
            </details>
            <div>
              <div className="text-[11.5px] mb-1" style={{ color: C.slate }}>External portal audiences</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {meta.audiences.external.map((a) => (
                  <label key={a.value} className="flex items-center gap-1.5 text-[12px] cursor-pointer" style={{ color: C.charcoal }}>
                    <Checkbox checked={form.audiences.includes(a.value)} onCheckedChange={() => toggleAud(a.value)} data-testid={`sop-aud-${a.value}`} /> {a.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-[12px]" style={{ color: C.slate }}>Related routes (one per line, e.g. /leads)</Label>
          <Textarea value={form.related_routes} onChange={(e) => set("related_routes", e.target.value)} rows={2} className="mt-1 text-[13px] font-mono" placeholder="/leads&#10;/crm/pipeline" />
        </div>

        <div data-testid="sop-form-steps">
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[12px]" style={{ color: C.slate }}>Steps</Label>
            <Button size="sm" variant="outline" onClick={addStep} className="h-7 gap-1 text-[12px]" data-testid="sop-add-step"><Plus size={12} /> Add step</Button>
          </div>
          <div className="space-y-2">
            {form.steps.map((s, i) => (
              <div key={i} className="flex gap-2 items-start rounded-lg border p-2" style={{ borderColor: C.line }}>
                <GripVertical size={14} className="mt-2 shrink-0" style={{ color: C.slate }} />
                <div className="flex-1 space-y-1.5">
                  <Input value={s.title} onChange={(e) => setStep(i, "title", e.target.value)} placeholder={`Step ${i + 1} title`} className="h-8 text-[13px]" data-testid={`sop-step-title-${i}`} />
                  <Textarea value={s.detail} onChange={(e) => setStep(i, "detail", e.target.value)} rows={2} placeholder="Detail" className="text-[13px]" />
                </div>
                {form.steps.length > 1 && <button onClick={() => rmStep(i)} className="p-1 mt-1 text-[#9C1E22]"><Trash2 size={14} /></button>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[12px]" style={{ color: C.slate }}>Guardrails / warnings (one per line)</Label>
          <Textarea value={form.warnings} onChange={(e) => set("warnings", e.target.value)} rows={2} className="mt-1 text-[13px]" placeholder="Do not share private client data." />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[12px]" style={{ color: C.slate }}>Who can do this</Label>
            <Textarea value={form.role_requirements} onChange={(e) => set("role_requirements", e.target.value)} rows={2} className="mt-1 text-[13px]" />
          </div>
          <div>
            <Label className="text-[12px]" style={{ color: C.slate }}>What to do next</Label>
            <Textarea value={form.next_steps} onChange={(e) => set("next_steps", e.target.value)} rows={2} className="mt-1 text-[13px]" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[12px]" style={{ color: C.slate }}>Links</Label>
            <Button size="sm" variant="outline" onClick={addLink} className="h-7 gap-1 text-[12px]"><Plus size={12} /> Add link</Button>
          </div>
          <div className="space-y-2">
            {form.links.map((l, i) => (
              <div key={i} className="flex gap-2">
                <Input value={l.label} onChange={(e) => setLink(i, "label", e.target.value)} placeholder="Label" className="h-8 text-[13px] w-1/3" />
                <Input value={l.url} onChange={(e) => setLink(i, "url", e.target.value)} placeholder="https://…" className="h-8 text-[13px] flex-1" />
                <button onClick={() => rmLink(i)} className="p-1 text-[#9C1E22]"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[12px]" style={{ color: C.slate }}>Screenshot URLs (one per line)</Label>
            <Textarea value={form.screenshots} onChange={(e) => set("screenshots", e.target.value)} rows={2} className="mt-1 text-[12px] font-mono" />
          </div>
          <div>
            <Label className="text-[12px]" style={{ color: C.slate }}>Video URLs (one per line)</Label>
            <Textarea value={form.videos} onChange={(e) => set("videos", e.target.value)} rows={2} className="mt-1 text-[12px] font-mono" />
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={() => navigate(editing ? `/sops/${id}` : "/sops")} className="h-9">Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="sop-save-btn" className="h-9 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editing ? "Save changes" : "Create draft"}
        </Button>
      </div>
      <p className="text-[11.5px] mt-2 text-right" style={{ color: C.slate }}>New and edited SOPs are never auto-published — publish from the SOP page after review.</p>
    </div>
  );
}
